import { useState, useCallback, useRef } from 'react';
import { searchByLottoPubblico, searchBySerialAndArticle, downloadDocument } from '../services/sharePointApi';

/**
 * Sort documents alphabetically by name
 * @param {Array} docs - Array of document objects
 * @returns {Array} - Sorted array
 */
const sortDocuments = (docs) => {
  return [...docs].sort((a, b) => (a.name || '').localeCompare(b.name || ''));
};

/**
 * Custom hook for document search functionality
 * @returns {Object} - Hook state and functions
 */
export const useDocumentSearch = () => {
  const [documents, setDocuments] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchPerformed, setSearchPerformed] = useState(false);
  const [productInfo, setProductInfo] = useState(null);
  const [isLoadingMore, setIsLoadingMore] = useState(false); // Still loading more pages
  const searchIdRef = useRef(0); // To prevent stale updates

  /**
   * Search for documents by lotto pubblico (serial number) and optionally by article code
    * 1. Searches configured Dataverse entity set by lotto pubblico (and article code if provided)
    * 2. Reads SchemaE values from Dataverse result
    * 3. For each non-empty SchemaE value, searches SharePoint documents by Nome
   * @param {string} lottoPubblico - The lotto pubblico (serial number) to search for
   * @param {string} [articleCode] - Optional article code to filter by (required for manual search)
   */
  const search = useCallback(async (lottoPubblico, articleCode = null) => {
    if (!lottoPubblico || lottoPubblico.trim() === '') {
      setError('Please enter a serial number');
      return;
    }

    // If articleCode is provided, it must not be empty
    if (articleCode !== null && articleCode.trim() === '') {
      setError('Please enter an article code');
      return;
    }

    setIsLoading(true);
    setIsLoadingMore(true);
    setError(null);
    setSearchPerformed(true);
    setDocuments([]);
    setProductInfo(null);
    
    // Increment search ID to invalidate any in-flight updates from previous searches
    const currentSearchId = ++searchIdRef.current;

    try {
      const progressCallback = (partialResult) => {
        // Only update if this is still the current search
        if (searchIdRef.current === currentSearchId) {
          setProductInfo(partialResult.productInfo);
          setDocuments(sortDocuments(partialResult.documents));
          // After first results arrive, show them but keep loading indicator
          setIsLoading(false);
        }
      };

      let result;
      
      if (articleCode) {
        // Manual search: search with both serial + article code (no fallback)
        result = await searchBySerialAndArticle(lottoPubblico.trim(), articleCode.trim(), progressCallback);
      } else {
        // AI scan or serial-only: search by serial only
        result = await searchByLottoPubblico(lottoPubblico.trim(), progressCallback);
      }
      
      // Final update with complete results (only if still current search)
      if (searchIdRef.current === currentSearchId) {
        setProductInfo(result.productInfo);
        setDocuments(sortDocuments(result.documents));
      }
    } catch (err) {
      if (searchIdRef.current === currentSearchId) {
        setError(err.message || 'An error occurred while searching for documents');
        setDocuments([]);
        setProductInfo(null);
      }
    } finally {
      if (searchIdRef.current === currentSearchId) {
        setIsLoading(false);
        setIsLoadingMore(false);
      }
    }
  }, []);

  /**
   * Download a specific document
   * @param {Object} document - The document object with name
   */
  const download = useCallback(async (document) => {
    try {
      // downloadDocument already returns a valid Blob from response.blob()
      const blob = await downloadDocument(document);
      
      // Use the blob directly without re-wrapping it
      // The blob already has the correct content-type from the API response
      const url = window.URL.createObjectURL(blob);
      const link = window.document.createElement('a');
      link.href = url;
      link.download = document.name || 'document';
      window.document.body.appendChild(link);
      link.click();
      window.document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      return true;
    } catch (err) {
      setError(err.message || 'An error occurred while downloading the document');
      return false;
    }
  }, []);

  /**
   * Clear search results and reset state
   */
  const clearResults = useCallback(() => {
    setDocuments([]);
    setError(null);
    setSearchPerformed(false);
    setProductInfo(null);
    setIsLoadingMore(false);
  }, []);

  return {
    documents,
    isLoading,
    isLoadingMore, // True when still loading more pages (shows "loading more..." indicator)
    error,
    searchPerformed,
    productInfo,
    search,
    download,
    clearResults,
  };
};

export default useDocumentSearch;
